import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Building2,
  CalendarRange,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Clock3,
  Filter,
  GitBranch,
  RotateCcw,
  Search,
  ShieldCheck,
  SlidersHorizontal,
  X,
} from "lucide-react";
import ValueHelpSelect from "./ValueHelpSelect";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import OrganizationHierarchy from "./OrganizationHierarchy";
import { buildRequesterHeaders } from "../utils/requester";

const statusToneMap = {
  Approved: "is-approved",
  Rejected: "is-rejected",
  Cancelled: "is-neutral",
  Pending: "is-pending",
};

const leaveStatusColors = {
  Approved: "#107e3e",
  Pending: "#f0ab00",
  Cancelled: "#4b5563",
  Rejected: "#5b738b",
};
const departmentChartPalette = ["#0a6ed1", "#188918", "#d97706", "#5b738b", "#7c3aed", "#14b8a6"];

const getTimeBasedGreeting = () => {
  const hour = new Date().getHours();

  if (hour >= 5 && hour < 12) return "Good morning";
  if (hour >= 12 && hour < 17) return "Good afternoon";
  if (hour >= 17 && hour < 21) return "Good evening";
  return "Good night";
};

const formatDate = (dateStr) => {
  if (!dateStr) return "Not available";

  try {
    const value = typeof dateStr === "object" && dateStr.$date ? dateStr.$date : dateStr;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "Not available";

    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "Not available";
  }
};

const shortLabel = (value, max = 12) => {
  if (!value) return "Unassigned";
  return value.length > max ? `${value.slice(0, max - 1)}…` : value;
};

const leaveCoverageForToday = (leave) => {
  if (leave.status !== "Approved") return false;

  const today = new Date().toISOString().split("T")[0];

  if (leave.is_partial_approval) {
    return leave.approved_start_date <= today && leave.approved_end_date >= today;
  }

  return leave.start_date <= today && leave.end_date >= today;
};

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;

  return (
    <div className="fiori-chart-tooltip">
      {label ? <div className="fiori-chart-tooltip-label">{label}</div> : null}
      {payload.map((entry) => (
        <div key={entry.name} className="fiori-chart-tooltip-row">
          <span>{entry.name}</span>
          <strong>{entry.value}</strong>
        </div>
      ))}
    </div>
  );
};

const OnLeaveEmployeesModal = ({ employees, onClose }) => {
  const groupedByType = employees.reduce((accumulator, employee) => {
    const type = employee.leave_type || "Other";
    accumulator[type] = accumulator[type] || [];
    accumulator[type].push(employee);
    return accumulator;
  }, {});

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div className="admin-modal admin-modal-wide" onClick={(event) => event.stopPropagation()}>
        <div className="admin-modal-header">
          <div>
            <div className="admin-section-overline">Daily workforce status</div>
            <h2>Employees on approved leave today</h2>
            <p>
              {new Date().toLocaleDateString("en-IN", {
                weekday: "long",
                day: "numeric",
                month: "long",
                year: "numeric",
              })}
            </p>
          </div>
          <button className="fiori-button secondary" onClick={onClose}>
            Close
          </button>
        </div>

        <div className="admin-dashboard-grid admin-dashboard-grid-compact">
          {Object.entries(groupedByType).map(([type, members]) => (
            <article key={type} className="fiori-stat-card">
              <div className="fiori-stat-label">{type}</div>
              <div className="fiori-stat-value">{members.length}</div>
              <div className="fiori-stat-note">Approved leave records</div>
            </article>
          ))}
        </div>

        <div className="fiori-panel">
          <div className="fiori-panel-header">
            <div>
              <h3>Leave roster</h3>
              <p>Current day absences with role and leave details</p>
            </div>
          </div>

          {employees.length === 0 ? (
            <div className="admin-empty-state">
              <CheckCircle2 size={28} />
              <div>
                <strong>No employees are on leave today</strong>
                <p>The workforce is fully available for the current day.</p>
              </div>
            </div>
          ) : (
            <div className="fiori-table-shell">
              <table className="fiori-table">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Department</th>
                    <th>Leave Type</th>
                    <th>From</th>
                    <th>To</th>
                    <th>Duration</th>
                  </tr>
                </thead>
                <tbody>
                  {employees.map((employee) => (
                    <tr key={employee._id}>
                      <td>
                        <div className="fiori-primary-cell">
                          <strong>{employee.employee_name || "Unknown employee"}</strong>
                          <span>{employee.employee_email || "No email"}</span>
                        </div>
                      </td>
                      <td>{employee.employee_department || "Unassigned"}</td>
                      <td>{employee.leave_type || "Not specified"}</td>
                      <td>{formatDate(employee.start_date)}</td>
                      <td>{formatDate(employee.end_date)}</td>
                      <td>{employee.days || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const AdminDashboard = ({ user, onNavigate }) => {
  const [filters, setFilters] = useState({
    department: "all",
    status: "all",
    leaveType: "all",
    timeRange: "all",
    dateFrom: "",
    dateTo: "",
    search: "",
  });
  const [adminIds, setAdminIds] = useState([]);
  const [expandedLeave, setExpandedLeave] = useState(null);
  const [rejectModal, setRejectModal] = useState({ show: false, leaveId: null, reason: "" });
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [showHierarchy, setShowHierarchy] = useState(false);
  const [showOnLeaveModal, setShowOnLeaveModal] = useState(false);
  const [isFilterCollapsed, setIsFilterCollapsed] = useState(false);
  const [allEmployees, setAllEmployees] = useState([]);
  const [allLeaves, setAllLeaves] = useState([]);

  const handleNavigate = useCallback((target, state = null) => {
    if (target) {
      onNavigate?.(target, state);
    }
  }, [onNavigate]);

  const fetchAdminData = async () => {
    try {
      setLoading(true);

      const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

      const employeesRes = await axios.get(
        `${backendUrl}/api/users/get_all_employees`,
        { headers: buildRequesterHeaders(user) }
      );
      const employees = Array.isArray(employeesRes.data)
        ? employeesRes.data.filter((employee) => employee && employee._id && employee.is_active !== false)
        : [];

      const adminsRes = await axios.get(`${backendUrl}/api/users/`);
      const adminIdsList = (adminsRes.data || [])
        .filter((admin) => admin.role === "Admin" && admin.is_active !== false)
        .map((admin) => admin._id);

      const allLeavesRes = await axios.get(`${backendUrl}/api/leaves/all?include_inactive=false`);
      const rawLeaves = Array.isArray(allLeavesRes.data) ? allLeavesRes.data : [];

      const activeEmployeeIds = new Set(employees.map((e) => String(e._id)));
      const activeEmployeeEmails = new Set(
        employees.map((e) => (e.email || "").toLowerCase().trim()).filter(Boolean)
      );

      const leaves = rawLeaves.filter((leave) => {
        const empId = leave.employee_id ? String(leave.employee_id) : "";
        const empEmail = (leave.employee_email || "").toLowerCase().trim();
        if (empId && activeEmployeeIds.has(empId)) return true;
        if (empEmail && activeEmployeeEmails.has(empEmail)) return true;
        return false;
      });

      setAllEmployees(employees);
      setAllLeaves(leaves);
      setAdminIds(adminIdsList);
    } catch (error) {
      setMessage("Unable to load administration dashboard data.");
      setAllEmployees([]);
      setAllLeaves([]);
      setAdminIds([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdminData();
  }, []);

  const departments = useMemo(() => {
    const set = new Set();
    allEmployees.forEach((emp) => {
      if (emp.department) set.add(emp.department.trim());
    });
    allLeaves.forEach((leave) => {
      if (leave.employee_department) set.add(leave.employee_department.trim());
    });
    return ["all", ...Array.from(set).sort()];
  }, [allEmployees, allLeaves]);

  const leaveTypes = useMemo(() => {
    const set = new Set(["Sick", "Planned", "Optional", "Compensatory Off", "Early Logout", "LWP"]);
    allLeaves.forEach((leave) => {
      if (leave.leave_type) set.add(leave.leave_type.trim());
    });
    return ["all", ...Array.from(set).sort()];
  }, [allLeaves]);

  const matchesTimeRange = useCallback((leave, rangeKey, dateFrom, dateTo) => {
    if (rangeKey === "all") return true;

    const leaveDates = [
      leave.start_date,
      leave.end_date,
      leave.applied_on,
      leave.approved_on,
    ].filter(Boolean).map(d => {
      const val = typeof d === "object" && d.$date ? d.$date : d;
      return new Date(val);
    }).filter(d => !Number.isNaN(d.getTime()));

    if (!leaveDates.length) return false;

    const now = new Date();
    const todayStr = now.toISOString().split("T")[0];

    if (rangeKey === "today") {
      return leaveCoverageForToday(leave) || leaveDates.some(d => d.toISOString().split("T")[0] === todayStr);
    }

    if (rangeKey === "this_week") {
      const firstDayOfWeek = new Date(now);
      const day = now.getDay();
      const diff = now.getDate() - day + (day === 0 ? -6 : 1);
      firstDayOfWeek.setDate(diff);
      firstDayOfWeek.setHours(0, 0, 0, 0);

      const lastDayOfWeek = new Date(firstDayOfWeek);
      lastDayOfWeek.setDate(firstDayOfWeek.getDate() + 6);
      lastDayOfWeek.setHours(23, 59, 59, 999);

      return leaveDates.some(d => d >= firstDayOfWeek && d <= lastDayOfWeek);
    }

    if (rangeKey === "this_month") {
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
      return leaveDates.some(d => d >= monthStart && d <= monthEnd);
    }

    if (rangeKey === "last_30_days") {
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      return leaveDates.some(d => d >= thirtyDaysAgo && d <= now);
    }

    if (rangeKey === "custom") {
      if (!dateFrom && !dateTo) return true;
      const start = dateFrom ? new Date(dateFrom) : new Date(0);
      const end = dateTo ? new Date(dateTo + "T23:59:59") : new Date(8640000000000000);
      return leaveDates.some(d => d >= start && d <= end);
    }

    return true;
  }, []);

  const activeEmployeeIdSet = useMemo(() => {
    return new Set(
      allEmployees
        .filter((emp) => emp.is_active !== false)
        .map((emp) => String(emp._id))
    );
  }, [allEmployees]);

  const activeEmployeeEmailSet = useMemo(() => {
    return new Set(
      allEmployees
        .filter((emp) => emp.is_active !== false)
        .map((emp) => (emp.email || "").toLowerCase().trim())
        .filter(Boolean)
    );
  }, [allEmployees]);

  const filteredEmployees = useMemo(() => {
    const q = filters.search.trim().toLowerCase();
    return allEmployees.filter((emp) => {
      if (emp.is_active === false) {
        return false;
      }
      if (filters.department !== "all" && emp.department !== filters.department) {
        return false;
      }
      if (q) {
        const matchesName = (emp.name || "").toLowerCase().includes(q);
        const matchesEmail = (emp.email || "").toLowerCase().includes(q);
        const matchesDesig = (emp.designation || "").toLowerCase().includes(q);
        if (!matchesName && !matchesEmail && !matchesDesig) return false;
      }
      return true;
    });
  }, [allEmployees, filters.department, filters.search]);

  const filteredLeaves = useMemo(() => {
    const q = filters.search.trim().toLowerCase();
    return allLeaves.filter((leave) => {
      const empId = leave.employee_id ? String(leave.employee_id) : "";
      const empEmail = (leave.employee_email || "").toLowerCase().trim();
      const isActiveEmp =
        (empId && activeEmployeeIdSet.has(empId)) ||
        (empEmail && activeEmployeeEmailSet.has(empEmail));

      if (!isActiveEmp) {
        return false;
      }
      if (filters.department !== "all" && leave.employee_department !== filters.department) {
        return false;
      }
      if (filters.status !== "all" && leave.status !== filters.status) {
        return false;
      }
      if (filters.leaveType !== "all" && leave.leave_type !== filters.leaveType) {
        return false;
      }
      if (!matchesTimeRange(leave, filters.timeRange, filters.dateFrom, filters.dateTo)) {
        return false;
      }
      if (q) {
        const matchesName = (leave.employee_name || "").toLowerCase().includes(q);
        const matchesEmail = (leave.employee_email || "").toLowerCase().includes(q);
        const matchesDesig = (leave.employee_designation || "").toLowerCase().includes(q);
        const matchesDept = (leave.employee_department || "").toLowerCase().includes(q);
        const matchesReason = (leave.reason || "").toLowerCase().includes(q);
        const matchesApprover = (leave.approved_by || "").toLowerCase().includes(q);
        if (!matchesName && !matchesEmail && !matchesDesig && !matchesDept && !matchesReason && !matchesApprover) {
          return false;
        }
      }
      return true;
    });
  }, [allLeaves, activeEmployeeIdSet, activeEmployeeEmailSet, filters, matchesTimeRange]);

  const scopedPendingLeaves = useMemo(() => {
    return filteredLeaves.filter((leave) => {
      const isTarget = adminIds.length ? adminIds.includes(leave.current_approver_id) : true;
      return isTarget && leave.status === "Pending";
    });
  }, [filteredLeaves, adminIds]);

  const scopedRecentActions = useMemo(() => {
    return filteredLeaves
      .filter((leave) => leave.status !== "Pending")
      .sort((first, second) => {
        const firstDate = first.approved_on || first.rejected_on || first.applied_on;
        const secondDate = second.approved_on || second.rejected_on || second.applied_on;
        return new Date(secondDate) - new Date(firstDate);
      })
      .slice(0, 8);
  }, [filteredLeaves]);

  const scopedEmployeesOnLeave = useMemo(() => {
    return filteredLeaves.filter(leaveCoverageForToday);
  }, [filteredLeaves]);

  const stats = useMemo(() => {
    const totalEmployees = filteredEmployees.length;
    const onLeaveToday = scopedEmployeesOnLeave.length;
    const workingToday = Math.max(0, totalEmployees - onLeaveToday);
    const pendingCount = scopedPendingLeaves.length;
    const approvedCount = filteredLeaves.filter((l) => l.status === "Approved").length;

    return {
      totalEmployees,
      pendingLeaves: pendingCount,
      onLeaveToday,
      workingToday,
      approvedLeaves: approvedCount,
    };
  }, [filteredEmployees, scopedEmployeesOnLeave, scopedPendingLeaves, filteredLeaves]);

  const hasActiveFilters = useMemo(() => {
    return (
      filters.department !== "all" ||
      filters.status !== "all" ||
      filters.leaveType !== "all" ||
      filters.timeRange !== "all" ||
      Boolean(filters.dateFrom) ||
      Boolean(filters.dateTo) ||
      Boolean(filters.search.trim())
    );
  }, [filters]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.department !== "all") count++;
    if (filters.status !== "all") count++;
    if (filters.leaveType !== "all") count++;
    if (filters.timeRange !== "all") count++;
    if (filters.dateFrom || filters.dateTo) count++;
    if (filters.search && filters.search.trim()) count++;
    return count;
  }, [filters]);

  const handleResetFilters = () => {
    setFilters({
      department: "all",
      status: "all",
      leaveType: "all",
      timeRange: "all",
      dateFrom: "",
      dateTo: "",
      search: "",
    });
  };

  const summaryCards = useMemo(
    () => [
      {
        title: "Working Today",
        value: stats.workingToday,
        note: filters.department !== "all" ? `Available in ${filters.department}` : "Available workforce for current operations",
        icon: ShieldCheck,
        linkLabel: "Review employee roster",
        action: () => handleNavigate("employees", {
          department: filters.department !== "all" ? filters.department : undefined,
          search: filters.search || undefined,
        }),
      },
      {
        title: "On Leave Today",
        value: stats.onLeaveToday,
        note: filters.department !== "all" ? `On leave in ${filters.department}` : "Approved absences for the current day",
        icon: CalendarRange,
        linkLabel: "View employees on leave",
        action: () => setShowOnLeaveModal(true),
      },
      {
        title: "Pending Approvals",
        value: stats.pendingLeaves,
        note: filters.department !== "all" ? `Awaiting review for ${filters.department}` : "Requests awaiting administration review",
        icon: Clock3,
        linkLabel: "Review pending requests",
        action: () => handleNavigate("leaves", {
          activeTab: "pending",
          statusFilter: "Pending",
          departmentFilter: filters.department !== "all" ? filters.department : undefined,
          typeFilter: filters.leaveType !== "all" ? filters.leaveType : undefined,
          searchTerm: filters.search || undefined,
        }),
      },
    ],
    [stats, filters, handleNavigate]
  );

  const departmentHeadcountData = useMemo(() => {
    const counts = filteredEmployees.reduce((accumulator, employee) => {
      const department = employee.department || "Unassigned";
      accumulator[department] = (accumulator[department] || 0) + 1;
      return accumulator;
    }, {});

    return Object.entries(counts)
      .map(([name, value], index) => ({
        name: shortLabel(name),
        fullName: name,
        value,
        color: departmentChartPalette[index % departmentChartPalette.length],
      }))
      .sort((first, second) => second.value - first.value)
      .slice(0, 6);
  }, [filteredEmployees]);

  const leaveStatusData = useMemo(() => {
    const statusOrder = ["Approved", "Pending", "Cancelled", "Rejected"];
    const counts = filteredLeaves.reduce((accumulator, leave) => {
      const status = leave.status || "Pending";
      accumulator[status] = (accumulator[status] || 0) + 1;
      return accumulator;
    }, {});

    return statusOrder
      .filter((status) => counts[status])
      .map((status) => ({
        name: status,
        value: counts[status],
        color: leaveStatusColors[status],
      }));
  }, [filteredLeaves]);

  const approvedLeavesCount = useMemo(
    () => filteredLeaves.filter((leave) => leave.status === "Approved").length,
    [filteredLeaves]
  );

  const monthlyTrendData = useMemo(() => {
    const months = [];
    const today = new Date();

    for (let index = 5; index >= 0; index -= 1) {
      const date = new Date(today.getFullYear(), today.getMonth() - index, 1);
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
      months.push({
        key,
        name: date.toLocaleDateString("en-IN", { month: "short" }),
        requests: 0,
        approved: 0,
      });
    }

    const monthMap = months.reduce((accumulator, month) => {
      accumulator[month.key] = month;
      return accumulator;
    }, {});

    filteredLeaves.forEach((leave) => {
      const appliedDate = new Date(leave.applied_on || leave.start_date);
      if (!Number.isNaN(appliedDate.getTime())) {
        const appliedKey = `${appliedDate.getFullYear()}-${String(appliedDate.getMonth() + 1).padStart(2, "0")}`;
        if (monthMap[appliedKey]) {
          monthMap[appliedKey].requests += 1;
        }
      }

      const approvalDate = new Date(leave.approved_on || "");
      if (!Number.isNaN(approvalDate.getTime())) {
        const approvalKey = `${approvalDate.getFullYear()}-${String(approvalDate.getMonth() + 1).padStart(2, "0")}`;
        if (monthMap[approvalKey] && leave.status === "Approved") {
          monthMap[approvalKey].approved += 1;
        }
      }
    });

    return months;
  }, [filteredLeaves]);

  const today = new Date().toLocaleDateString("en-IN", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  const updateStatus = async (leaveId, status, rejectionReason = "") => {
    try {
      const payload = {
        status,
        approved_by: user?.name || user?.email || "Administrator",
      };

      if (status === "Rejected") {
        payload.rejection_reason = rejectionReason;
      }

      const response = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/leaves/update_status/${leaveId}`,
        payload,
        { headers: buildRequesterHeaders(user) }
      );

      if (response.status === 200) {
        setMessage(response.data?.message || `Leave ${status.toLowerCase()} successfully.`);
        setRejectModal({ show: false, leaveId: null, reason: "" });
        fetchAdminData();
        setTimeout(() => setMessage(""), 3000);
      }
    } catch (error) {
      setMessage(error.response?.data?.error || "Unable to update leave status.");
      setTimeout(() => setMessage(""), 3000);
    }
  };

  const confirmReject = async () => {
    if (!rejectModal.reason.trim()) {
      setMessage("A rejection reason is required.");
      setTimeout(() => setMessage(""), 3000);
      return;
    }

    await updateStatus(rejectModal.leaveId, "Rejected", rejectModal.reason);
  };

  if (loading) {
    return (
      <section className="admin-dashboard admin-dashboard-loading">
        <div className="fiori-loading-card">
          <Clock3 size={28} />
          <div>
            <strong>Loading administration workspace</strong>
            <p>Preparing workforce metrics and approval data.</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="admin-dashboard">
      <header className="admin-hero admin-hero-command">
        <div className="admin-hero-copy">
          <div className="admin-section-overline">Administration Overview</div>
          <h1>
            {getTimeBasedGreeting()}, {user?.name?.split(" ")[0] || "Administrator"}
          </h1>
          <p>
            Centralize leave decisions, workforce visibility, and governance signals from one
            executive-style operations workspace.
          </p>

          <div className="admin-hero-pill-row">
            <button
              className="admin-hero-pill"
              onClick={() => handleNavigate("leaves", {
                activeTab: "pending",
                statusFilter: "Pending",
                departmentFilter: filters.department !== "all" ? filters.department : undefined,
                typeFilter: filters.leaveType !== "all" ? filters.leaveType : undefined,
                searchTerm: filters.search || undefined,
              })}
            >
              <Clock3 size={14} />
              <span>{stats.pendingLeaves} pending approvals</span>
            </button>
            <button
              className="admin-hero-pill"
              onClick={() => handleNavigate("employees", {
                department: filters.department !== "all" ? filters.department : undefined,
                search: filters.search || undefined,
              })}
            >
              <ShieldCheck size={14} />
              <span>{stats.workingToday} working today</span>
            </button>
            <button className="admin-hero-pill" onClick={() => setShowOnLeaveModal(true)}>
              <CalendarRange size={14} />
              <span>{stats.onLeaveToday} on leave today</span>
            </button>
          </div>

          <div className="admin-hero-actions">
            <button
              className="fiori-button primary"
              onClick={() => handleNavigate("leaves", {
                activeTab: "pending",
                statusFilter: "Pending",
                departmentFilter: filters.department !== "all" ? filters.department : undefined,
                typeFilter: filters.leaveType !== "all" ? filters.leaveType : undefined,
                searchTerm: filters.search || undefined,
              })}
            >
              Review approvals
            </button>
            <button
              className="fiori-button secondary"
              onClick={() => handleNavigate("employees", {
                department: filters.department !== "all" ? filters.department : undefined,
                search: filters.search || undefined,
              })}
            >
              Open employee directory
            </button>
            <button className="fiori-button secondary" onClick={() => setShowHierarchy(true)}>
              View organization hierarchy
            </button>
          </div>
        </div>

        <div className="admin-hero-meta">
          <div className="admin-hero-meta-item">
            <span>Role</span>
            <strong>{user?.designation || "Administrator"}</strong>
          </div>
          <div className="admin-hero-meta-item">
            <span>Department</span>
            <strong>{user?.department || "Human Resources"}</strong>
          </div>
          <div className="admin-hero-meta-item">
            <span>Date</span>
            <strong>{today}</strong>
          </div>
          <div
            className="admin-hero-meta-item is-clickable"
            onClick={() => handleNavigate("leaves", {
              activeTab: "records",
              statusFilter: "Approved",
              departmentFilter: filters.department !== "all" ? filters.department : undefined,
              typeFilter: filters.leaveType !== "all" ? filters.leaveType : undefined,
              searchTerm: filters.search || undefined,
            })}
            title="Click to view all approved leaves"
          >
            <span>Approved leaves</span>
            <strong>{approvedLeavesCount}</strong>
          </div>
        </div>
      </header>

      {/* Admin Dashboard Filter Toolbar */}
      <div className="admin-dashboard-filter-card">
        <div className="admin-filter-head">
          <div className="admin-filter-head-title">
            <Filter size={18} className="admin-filter-icon" />
            <div>
              <div className="admin-filter-title-row">
                <h3>Admin Dashboard Filters</h3>
                {activeFilterCount > 0 && (
                  <span className="admin-filter-active-counter-badge">
                    {activeFilterCount} active
                  </span>
                )}
              </div>
              <p>Filter workforce metrics, analytics, pending approvals, and decisions</p>
            </div>
          </div>
          <div className="admin-filter-head-actions">
            {hasActiveFilters && (
              <button
                type="button"
                className="admin-filter-reset-btn"
                onClick={handleResetFilters}
                title="Reset all active filters"
              >
                <RotateCcw size={14} />
                <span>Reset Filters</span>
              </button>
            )}
            <button
              type="button"
              className="admin-filter-toggle-btn"
              onClick={() => setIsFilterCollapsed((prev) => !prev)}
              aria-expanded={!isFilterCollapsed}
              title={isFilterCollapsed ? "Expand filters" : "Collapse filters"}
            >
              <SlidersHorizontal size={14} />
              <span>{isFilterCollapsed ? "Show Filters" : "Hide Filters"}</span>
              {isFilterCollapsed ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
            </button>
          </div>
        </div>

        <div className={`admin-filter-body-collapsible ${isFilterCollapsed ? "is-collapsed" : ""}`}>
          <div className="admin-filter-controls-grid">
            {/* Search Field */}
            <div className="admin-filter-field admin-filter-search">
              <label htmlFor="admin-search-input">Search Records</label>
              <div className="admin-search-input-wrap">
                <Search size={16} className="admin-search-icon" />
                <input
                  id="admin-search-input"
                  type="text"
                  placeholder="Search employee, email, role..."
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                />
                {filters.search && (
                  <button
                    type="button"
                    className="admin-clear-input-btn"
                    onClick={() => setFilters(prev => ({ ...prev, search: "" }))}
                    aria-label="Clear search"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            </div>

            {/* Department Filter */}
            <div className="admin-filter-field">
              <label>Department</label>
              <ValueHelpSelect
                value={filters.department}
                onChange={(val) => setFilters(prev => ({ ...prev, department: val }))}
                searchPlaceholder="Search departments"
                options={[
                  { value: "all", label: `All Departments (${departments.length > 1 ? departments.length - 1 : 0})` },
                  ...departments.filter(d => d !== "all").map(dept => ({ value: dept, label: dept })),
                ]}
              />
            </div>

            {/* Status Filter */}
            <div className="admin-filter-field">
              <label>Leave Status</label>
              <ValueHelpSelect
                value={filters.status}
                onChange={(val) => setFilters(prev => ({ ...prev, status: val }))}
                searchPlaceholder="Search statuses"
                options={[
                  { value: "all", label: "All Statuses" },
                  { value: "Pending", label: "Pending Approvals" },
                  { value: "Approved", label: "Approved" },
                  { value: "Rejected", label: "Rejected" },
                  { value: "Cancelled", label: "Cancelled" },
                ]}
              />
            </div>

            {/* Leave Type Filter */}
            <div className="admin-filter-field">
              <label>Leave Type</label>
              <ValueHelpSelect
                value={filters.leaveType}
                onChange={(val) => setFilters(prev => ({ ...prev, leaveType: val }))}
                searchPlaceholder="Search leave types"
                options={[
                  { value: "all", label: "All Types" },
                  ...leaveTypes.filter(t => t !== "all").map(type => ({ value: type, label: type })),
                ]}
              />
            </div>

            {/* Time Range Preset */}
            <div className="admin-filter-field">
              <label>Time Period</label>
              <ValueHelpSelect
                value={filters.timeRange}
                onChange={(val) => setFilters(prev => ({ ...prev, timeRange: val }))}
                searchPlaceholder="Search time periods"
                options={[
                  { value: "all", label: "All Time" },
                  { value: "today", label: "Today" },
                  { value: "this_week", label: "This Week" },
                  { value: "this_month", label: "This Month" },
                  { value: "last_30_days", label: "Past 30 Days" },
                  { value: "custom", label: "Custom Date Range" },
                ]}
              />
            </div>

            {/* Custom Date Range */}
            {filters.timeRange === "custom" && (
              <>
                <div className="admin-filter-field">
                  <label htmlFor="admin-date-from">From Date</label>
                  <input
                    id="admin-date-from"
                    type="date"
                    value={filters.dateFrom}
                    onChange={(e) => setFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
                  />
                </div>
                <div className="admin-filter-field">
                  <label htmlFor="admin-date-to">To Date</label>
                  <input
                    id="admin-date-to"
                    type="date"
                    value={filters.dateTo}
                    onChange={(e) => setFilters(prev => ({ ...prev, dateTo: e.target.value }))}
                  />
                </div>
              </>
            )}
          </div>
        </div>

        {/* Active Filter Pills & Summary Scope Banner */}
        {hasActiveFilters && (
          <div className="admin-filter-active-bar">
            <div className="admin-filter-active-pills">
              <span className="admin-active-label">Active filters:</span>
              {filters.department !== "all" && (
                <button
                  type="button"
                  className="admin-active-pill"
                  onClick={() => setFilters(prev => ({ ...prev, department: "all" }))}
                  title="Remove department filter"
                >
                  Dept: {filters.department} <X size={12} />
                </button>
              )}
              {filters.status !== "all" && (
                <button
                  type="button"
                  className="admin-active-pill"
                  onClick={() => setFilters(prev => ({ ...prev, status: "all" }))}
                  title="Remove status filter"
                >
                  Status: {filters.status} <X size={12} />
                </button>
              )}
              {filters.leaveType !== "all" && (
                <button
                  type="button"
                  className="admin-active-pill"
                  onClick={() => setFilters(prev => ({ ...prev, leaveType: "all" }))}
                  title="Remove leave type filter"
                >
                  Type: {filters.leaveType} <X size={12} />
                </button>
              )}
              {filters.timeRange !== "all" && (
                <button
                  type="button"
                  className="admin-active-pill"
                  onClick={() => setFilters(prev => ({ ...prev, timeRange: "all", dateFrom: "", dateTo: "" }))}
                  title="Remove time range filter"
                >
                  Period: {filters.timeRange.replace("_", " ")} <X size={12} />
                </button>
              )}
              {Boolean(filters.search.trim()) && (
                <button
                  type="button"
                  className="admin-active-pill"
                  onClick={() => setFilters(prev => ({ ...prev, search: "" }))}
                  title="Remove search query"
                >
                  Search: "{filters.search}" <X size={12} />
                </button>
              )}
            </div>
            <div className="admin-filter-scope-badge">
              Showing filter-related data only: <strong>{filteredEmployees.length}</strong> of {allEmployees.length} employees · <strong>{filteredLeaves.length}</strong> of {allLeaves.length} leaves
            </div>
          </div>
        )}
      </div>

      <div className="admin-dashboard-grid admin-overview-grid">
        {summaryCards.map((card) => {
          const Icon = card.icon;
          return (
            <article
              key={card.title}
              className="fiori-stat-card is-actionable"
              onClick={card.action}
              role="button"
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  card.action();
                }
              }}
            >
              <div className="fiori-stat-topline">
                <span className="fiori-stat-label">{card.title}</span>
                <Icon size={18} />
              </div>
              <div className="fiori-stat-value">{card.value}</div>
              <div className="fiori-stat-note">{card.note}</div>
              <div className="fiori-inline-link">{card.linkLabel}</div>
            </article>
          );
        })}
      </div>

      <div className="admin-analytics-grid">
        <article className="fiori-panel fiori-chart-card">
          <div className="fiori-panel-header">
            <div>
              <h3>Department headcount</h3>
              <p>
                {filters.department !== "all"
                  ? `Filtered to ${filters.department}`
                  : "Click any department bar to view its employees"}
              </p>
            </div>
            <button
              type="button"
              className="fiori-card-link-button"
              onClick={() => handleNavigate("employees", {
                department: filters.department !== "all" ? filters.department : undefined,
                search: filters.search || undefined,
              })}
            >
              Open employee directory →
            </button>
          </div>
          <div className="fiori-chart-shell">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={departmentHeadcountData}
                barCategoryGap={22}
                margin={{ top: 8, right: 8, left: -10, bottom: 0 }}
                onClick={(state) => {
                  if (state && state.activePayload && state.activePayload[0]) {
                    const item = state.activePayload[0].payload;
                    const dept = item.fullName || item.name;
                    handleNavigate("employees", { department: dept });
                  }
                }}
              >
                <CartesianGrid stroke="#e8edf3" vertical={false} />
                <XAxis dataKey="name" tickLine={false} axisLine={false} tickMargin={10} />
                <YAxis allowDecimals={false} tickLine={false} axisLine={false} tickMargin={10} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                  {departmentHeadcountData.map((entry) => (
                    <Cell
                      key={entry.name}
                      fill={entry.color}
                      cursor="pointer"
                      onClick={(e) => {
                        e?.stopPropagation?.();
                        const dept = entry.fullName || entry.name;
                        handleNavigate("employees", { department: dept });
                      }}
                      opacity={filters.department === "all" || filters.department === entry.fullName ? 1 : 0.35}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </article>

        <article className="fiori-panel fiori-chart-card">
          <div className="fiori-panel-header">
            <div>
              <h3>Leave status breakdown</h3>
              <p>
                {filters.status !== "all"
                  ? `Filtered to ${filters.status}`
                  : "Click a slice to view matching leave records"}
              </p>
            </div>
            <button
              type="button"
              className="fiori-card-link-button"
              onClick={() => handleNavigate("leaves", {
                activeTab: filters.status === "Pending" ? "pending" : "records",
                statusFilter: filters.status !== "all" ? filters.status : undefined,
                departmentFilter: filters.department !== "all" ? filters.department : undefined,
                typeFilter: filters.leaveType !== "all" ? filters.leaveType : undefined,
                searchTerm: filters.search || undefined,
              })}
            >
              Open leave workspace →
            </button>
          </div>
          <div className="fiori-chart-shell">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart margin={{ top: 4, right: 8, bottom: 4, left: 8 }}>
                <Pie
                  data={leaveStatusData}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={62}
                  outerRadius={94}
                  paddingAngle={3}
                  onClick={(entry) => {
                    if (entry && entry.name) {
                      const status = entry.name;
                      handleNavigate("leaves", {
                        activeTab: status === "Pending" ? "pending" : "records",
                        statusFilter: status,
                        departmentFilter: filters.department !== "all" ? filters.department : undefined,
                      });
                    }
                  }}
                  cursor="pointer"
                >
                  {leaveStatusData.map((entry) => (
                    <Cell
                      key={entry.name}
                      fill={entry.color}
                      cursor="pointer"
                      onClick={(e) => {
                        e?.stopPropagation?.();
                        const status = entry.name;
                        handleNavigate("leaves", {
                          activeTab: status === "Pending" ? "pending" : "records",
                          statusFilter: status,
                          departmentFilter: filters.department !== "all" ? filters.department : undefined,
                        });
                      }}
                      opacity={filters.status === "all" || filters.status === entry.name ? 1 : 0.35}
                    />
                  ))}
                </Pie>
                <Tooltip content={<ChartTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </article>

        <article className="fiori-panel fiori-chart-card is-clickable" onClick={() => handleNavigate("logs")}>
          <div className="fiori-panel-header">
            <div>
              <h3>Monthly leave activity</h3>
              <p>Submitted versus approved requests over the last six months</p>
            </div>
            <div className="fiori-card-link">Open audit logs</div>
          </div>
          <div className="fiori-chart-shell">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={monthlyTrendData} margin={{ top: 8, right: 8, left: -10, bottom: 0 }}>
                <CartesianGrid stroke="#e8edf3" vertical={false} />
                <XAxis dataKey="name" tickLine={false} axisLine={false} tickMargin={10} />
                <YAxis allowDecimals={false} tickLine={false} axisLine={false} tickMargin={10} />
                <Tooltip content={<ChartTooltip />} />
                <Area
                  type="monotone"
                  dataKey="requests"
                  stroke="#0a6ed1"
                  fill="rgba(10, 110, 209, 0.18)"
                  strokeWidth={2}
                  name="Requests"
                />
                <Area
                  type="monotone"
                  dataKey="approved"
                  stroke="#5b738b"
                  fill="rgba(91, 115, 139, 0.14)"
                  strokeWidth={2}
                  name="Approved"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </article>
      </div>

      <div className="admin-dashboard-layout">
        <div className="admin-dashboard-primary">
          <section className="fiori-panel">
            <div className="fiori-panel-header">
              <div>
                <h3>Pending leave approvals</h3>
                <p>Administration-level requests escalated for action</p>
              </div>
              <div className="fiori-counter">{scopedPendingLeaves.length}</div>
            </div>

            {scopedPendingLeaves.length === 0 ? (
              <div className="admin-empty-state">
                <CheckCircle2 size={28} />
                <div>
                  <strong>No pending leave approvals {hasActiveFilters ? "matching active filters" : ""}</strong>
                  <p>{hasActiveFilters ? "Try clearing or adjusting filters to view other requests." : "All escalated requests have been processed."}</p>
                  {hasActiveFilters && (
                    <button
                      className="fiori-button secondary"
                      onClick={handleResetFilters}
                      style={{ marginTop: 10 }}
                    >
                      Reset Filters
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="admin-approval-list">
                {scopedPendingLeaves.map((leave) => (
                  <article
                    key={leave._id}
                    className="admin-approval-card is-clickable"
                    onClick={() => handleNavigate("leaves", {
                      activeTab: "pending",
                      statusFilter: "Pending",
                      leaveId: leave._id,
                    })}
                  >
                    <div className="admin-approval-card-header">
                      <div>
                        <h4>{leave.employee_name || "Unknown employee"}</h4>
                        <p>
                          {leave.employee_designation || "Role not set"} |{" "}
                          {leave.employee_department || "Department not set"}
                        </p>
                      </div>
                      <button
                        className="fiori-inline-button"
                        onClick={(event) => {
                          event.stopPropagation();
                          setExpandedLeave(expandedLeave === leave._id ? null : leave._id);
                        }}
                      >
                        {expandedLeave === leave._id ? "Hide details" : "Show details"}
                        <ChevronRight size={16} />
                      </button>
                    </div>

                    <div className="admin-approval-metadata">
                      <span>{leave.leave_type || "Leave"}</span>
                      <span>{leave.days} day(s)</span>
                      <span>{formatDate(leave.start_date)} to {formatDate(leave.end_date)}</span>
                    </div>

                    {expandedLeave === leave._id && (
                      <div className="admin-approval-details">
                        <div>
                          <span>Requested On</span>
                          <strong>{formatDate(leave.applied_on)}</strong>
                        </div>
                        <div>
                          <span>Escalation Level</span>
                          <strong>{leave.escalation_level ?? 0}</strong>
                        </div>
                        <div className="is-wide">
                          <span>Reason</span>
                          <strong>{leave.reason || "No reason provided"}</strong>
                        </div>
                      </div>
                    )}

                    <div className="admin-approval-actions">
                      <button
                        className="fiori-button primary"
                        onClick={(event) => {
                          event.stopPropagation();
                          updateStatus(leave._id, "Approved");
                        }}
                      >
                        Approve
                      </button>
                      <button
                        className="fiori-button secondary danger"
                        onClick={(event) => {
                          event.stopPropagation();
                          setRejectModal({ show: true, leaveId: leave._id, reason: "" });
                        }}
                      >
                        Reject
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>

          <section className="fiori-panel is-clickable" onClick={() => handleNavigate("logs")}>
            <div className="fiori-panel-header">
              <div>
                <h3>Recent decision log</h3>
                <p>Latest approved and rejected leave actions</p>
              </div>
              <div className="fiori-card-link">Open audit logs</div>
            </div>

            {scopedRecentActions.length === 0 ? (
              <div className="admin-empty-state">
                <GitBranch size={28} />
                <div>
                  <strong>No recent leave decisions {hasActiveFilters ? "matching active filters" : ""}</strong>
                  <p>{hasActiveFilters ? "No decisions match the current filter selection." : "Approved and rejected requests will appear here."}</p>
                </div>
              </div>
            ) : (
              <div className="fiori-table-shell">
                <table className="fiori-table">
                  <thead>
                    <tr>
                      <th>Employee</th>
                      <th>Leave Type</th>
                      <th>Period</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scopedRecentActions.map((action) => (
                      <tr key={action._id}>
                        <td>
                          <div className="fiori-primary-cell">
                            <strong>{action.employee_name || "Unknown employee"}</strong>
                            <span>{formatDate(action.approved_on || action.rejected_on || action.applied_on)}</span>
                          </div>
                        </td>
                        <td>{action.leave_type || "Leave"}</td>
                        <td>
                          {formatDate(action.start_date)} to {formatDate(action.end_date)}
                        </td>
                        <td>
                          <span className={`fiori-status-pill ${statusToneMap[action.status] || "is-neutral"}`}>
                            {action.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>

        <aside className="admin-dashboard-secondary">
          <section className="fiori-panel">
            <div className="fiori-panel-header">
              <div>
                <h3>Administration priorities</h3>
                <p>Operational indicators for daily review</p>
              </div>
            </div>

            <div className="admin-priority-list">
              <div
                className="admin-priority-item is-clickable"
                onClick={() => handleNavigate("employees", {
                  department: filters.department !== "all" ? filters.department : undefined,
                  search: filters.search || undefined,
                })}
              >
                <Building2 size={18} />
                <div>
                  <strong>Workforce availability</strong>
                  <p>{stats.workingToday} employees are currently available for allocation.</p>
                </div>
              </div>
              <div
                className="admin-priority-item is-clickable"
                onClick={() => handleNavigate("leaves", {
                  activeTab: "pending",
                  statusFilter: "Pending",
                  departmentFilter: filters.department !== "all" ? filters.department : undefined,
                  typeFilter: filters.leaveType !== "all" ? filters.leaveType : undefined,
                  searchTerm: filters.search || undefined,
                })}
              >
                <Clock3 size={18} />
                <div>
                  <strong>Approval queue</strong>
                  <p>{stats.pendingLeaves} requests require review at the administration layer.</p>
                </div>
              </div>
              <div className="admin-priority-item is-clickable" onClick={() => setShowOnLeaveModal(true)}>
                <CalendarRange size={18} />
                <div>
                  <strong>Approved absences</strong>
                  <p>{stats.onLeaveToday} employees are marked unavailable for the current day.</p>
                </div>
              </div>
            </div>
          </section>

          <section className="fiori-panel is-clickable" onClick={() => setShowHierarchy(true)}>
            <div className="fiori-panel-header">
              <div>
                <h3>Organization governance</h3>
                <p>Access the enterprise reporting structure</p>
              </div>
              <div className="fiori-card-link">Open organization hierarchy</div>
            </div>
            <button
              className="fiori-button primary full-width"
              style={{ width: "100%", whiteSpace: "normal", textAlign: "center", wordBreak: "break-word" }}
              onClick={(event) => {
                event.stopPropagation();
                setShowHierarchy(true);
              }}
            >
              Open organization hierarchy
            </button>
          </section>
        </aside>
      </div>

      {message && (
        <div
          className={`admin-toast ${message.toLowerCase().includes("unable") || message.toLowerCase().includes("required") ? "is-error" : "is-success"}`}
        >
          {message}
        </div>
      )}

      {showHierarchy && <OrganizationHierarchy user={user} onClose={() => setShowHierarchy(false)} />}

      {showOnLeaveModal && (
        <OnLeaveEmployeesModal
          employees={scopedEmployeesOnLeave}
          onClose={() => setShowOnLeaveModal(false)}
        />
      )}

      {rejectModal.show && (
        <div
          className="admin-modal-overlay"
          onClick={() => setRejectModal({ show: false, leaveId: null, reason: "" })}
        >
          <div className="admin-modal" onClick={(event) => event.stopPropagation()}>
            <div className="admin-modal-header">
              <div>
                <div className="admin-section-overline">Leave action</div>
                <h2>Reject leave request</h2>
                <p>Provide a clear audit reason for the decision.</p>
              </div>
              <button
                className="fiori-button secondary"
                onClick={() => setRejectModal({ show: false, leaveId: null, reason: "" })}
              >
                Cancel
              </button>
            </div>

            <div className="fiori-form-field">
              <label htmlFor="reject-reason">Rejection reason</label>
              <textarea
                id="reject-reason"
                placeholder="Enter the business reason for rejection"
                value={rejectModal.reason}
                onChange={(event) =>
                  setRejectModal((previous) => ({ ...previous, reason: event.target.value }))
                }
                rows={6}
              />
            </div>

            <div className="admin-modal-actions">
              <button
                className="fiori-button secondary"
                onClick={() => setRejectModal({ show: false, leaveId: null, reason: "" })}
              >
                Dismiss
              </button>
              <button
                className="fiori-button primary danger"
                onClick={confirmReject}
                disabled={!rejectModal.reason.trim()}
              >
                Confirm rejection
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default AdminDashboard;
